import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Lenis from '@studio-freight/lenis';
import { Clock, MapPin, Phone, Envelope } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Sparkles } from 'lucide-react';

const BeautySalonDemo = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smooth: true
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  const services = [
    {
      category: 'Threading',
      image: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800&q=80',
      services: ['Eyebrow Threading', 'Upper Lip', 'Full Face', 'Chin'],
      priceRange: 'Starting at $8'
    },
    {
      category: 'Body Waxing',
      image: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=800&q=80',
      services: ['Brazilian', 'Bikini', 'Full Legs', 'Arms', 'Underarm', 'Back', 'Chest'],
      priceRange: 'Starting at $25'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-paper/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center shadow-sm">
              <Sparkles size={24} className="text-accent" />
            </div>
            <div>
              <h1 className="text-base sm:text-xl font-serif font-semibold tracking-tight">Glow Beauty Studio</h1>
              <p className="text-xs text-foreground-secondary">Threading & Waxing</p>
            </div>
          </div>
          
          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={() => navigate('/demos/beauty-salon')}
              className="text-sm font-medium text-foreground-secondary hover:text-accent transition-colors"
            >
              Home
            </button>
            <button
              onClick={() => {
                const section = document.getElementById('our-services');
                section?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-sm font-medium text-foreground-secondary hover:text-accent transition-colors"
            >
              Our Services
            </button>
            <button
              onClick={() => {
                const section = document.getElementById('testimonials');
                section?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-sm font-medium text-foreground-secondary hover:text-accent transition-colors"
            >
              Testimonials
            </button>
            <button
              onClick={() => {
                const section = document.getElementById('contact-us');
                section?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-sm font-medium text-foreground-secondary hover:text-accent transition-colors"
            >
              Contact Us
            </button>
          </div>

          <Button
            data-testid="book-appointment-nav-btn"
            onClick={() => navigate('/demos/beauty-salon/booking')}
            className="bg-primary text-primary-foreground hover:bg-primary/90 text-sm sm:text-base"
          >
            Book Now
          </Button>
        </div>
      </nav>

      <section className="relative min-h-screen flex items-center justify-center pt-20">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=1600&q=80"
            alt="Beauty salon professional treatment"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background"></div>
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative z-10 text-center px-6 max-w-4xl mx-auto"
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-white mb-6 tracking-tight">
            Your Beauty,
            <br />
            Perfected
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Professional threading and waxing services in a serene, relaxing environment
          </p>
          <Button
            data-testid="book-now-hero-btn"
            size="lg"
            onClick={() => navigate('/demos/beauty-salon/booking')}
            className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 hover:-translate-y-0.5 transition-transform"
          >
            Book Your Appointment
          </Button>
        </motion.div>
      </section>

      <section id="our-services" className="py-24 sm:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h3 className="text-3xl sm:text-4xl font-serif font-medium mb-4 tracking-tight">
              Our Services
            </h3>
            <p className="text-foreground-secondary text-base sm:text-lg max-w-2xl mx-auto">
              Professional beauty treatments tailored to enhance your natural elegance
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {services.map((service, idx) => (
              <motion.div
                key={service.category}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
              >
                <div className="group relative overflow-hidden rounded-md border border-border bg-background-paper hover:-translate-y-1 transition-transform duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={service.image}
                      alt={`${service.category} services`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 sm:p-8">
                    <h4 className="text-xl sm:text-2xl font-serif font-medium mb-3">{service.category}</h4>
                    <ul className="space-y-2 mb-4">
                      {service.services.map((s) => (
                        <li key={s} className="text-sm sm:text-base text-foreground-secondary flex items-center gap-2">
                          <span className="w-1 h-1 rounded-full bg-accent"></span>
                          {s}
                        </li>
                      ))}
                    </ul>
                    <p className="text-accent font-medium">{service.priceRange}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="testimonials" className="py-24 sm:py-32 px-6 bg-background">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h3 className="text-3xl sm:text-4xl font-serif font-medium mb-4 tracking-tight">
              What Our Clients Say
            </h3>
            <p className="text-foreground-secondary text-base sm:text-lg max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                name: "Amanda K.",
                text: "Very professional service and clean environment. The staff is friendly and skilled. Highly recommend!",
                rating: 5
              },
              {
                name: "Rachel T.",
                text: "Best threading experience I've had! The results are always perfect and the prices are very reasonable.",
                rating: 5
              },
              {
                name: "Nicole P.",
                text: "Finally found my go-to place for waxing. Professional, clean, and the results last so long. Love it here!",
                rating: 5
              }
            ].map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="bg-background-paper border border-border rounded-md p-6 sm:p-8"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-accent text-lg">★</span>
                  ))}
                </div>
                <p className="text-foreground-secondary mb-4 text-sm sm:text-base italic">
                  "{testimonial.text}"
                </p>
                <p className="font-medium text-sm sm:text-base">— {testimonial.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact-us" className="py-32 px-6 bg-background-subtle">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-3xl sm:text-4xl font-serif font-medium mb-8 tracking-tight">
              Visit Us Today
            </h3>
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="text-left bg-background-paper p-8 rounded-md border border-border">
                <h4 className="font-medium text-xl mb-4">Location</h4>
                <div className="space-y-3 text-foreground-secondary">
                  <div className="flex items-start gap-3">
                    <MapPin size={20} weight="light" className="text-accent mt-1 flex-shrink-0" />
                    <div>
                      <p>123 Main Street</p>
                      <p>Dallas, GA 30132</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={20} weight="light" className="text-accent flex-shrink-0" />
                    <a href="tel:7705551234" className="hover:text-accent transition-colors">(770) 555-1234</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Envelope size={20} weight="light" className="text-accent flex-shrink-0" />
                    <a href="mailto:info@glowbeautystudio.com" className="hover:text-accent transition-colors">info@glowbeautystudio.com</a>
                  </div>
                </div>
              </div>
              <div className="text-left bg-background-paper p-8 rounded-md border border-border">
                <h4 className="font-medium text-xl mb-4">Business Hours</h4>
                <div className="space-y-2 text-foreground-secondary">
                  <div className="flex justify-between">
                    <span>Monday - Saturday</span>
                    <span className="font-medium">10am - 7pm</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday</span>
                    <span className="font-medium">12pm - 5pm</span>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-foreground-secondary text-base sm:text-lg mb-8 max-w-2xl mx-auto">
              Conveniently located in Dallas, Georgia. Walk-ins welcome, but online booking ensures your preferred time!
            </p>
            <Button
              data-testid="book-appointment-location-btn"
              size="lg"
              onClick={() => navigate('/demos/beauty-salon/booking')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 text-lg px-8 py-6 hover:-translate-y-0.5 transition-transform"
            >
              Book Your Appointment
            </Button>
          </motion.div>
        </div>
      </section>

      <section className="py-32 px-6 bg-background-subtle">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-3xl sm:text-4xl font-serif font-medium mb-8 tracking-tight">
              Ready to Experience Excellence?
            </h3>
            <p className="text-foreground-secondary text-base sm:text-lg mb-8 max-w-2xl mx-auto">
              Book your appointment today and discover why our clients trust us with their beauty needs
            </p>
            <Button
              data-testid="book-appointment-cta-btn"
              size="lg"
              onClick={() => navigate('/demos/beauty-salon/booking')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 text-lg px-8 py-6 hover:-translate-y-0.5 transition-transform"
            >
              Book Now
            </Button>
          </motion.div>
        </div>
      </section>

      <footer className="py-12 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center shadow-sm">
                <Sparkles size={24} className="text-accent" />
              </div>
              <div>
                <h4 className="text-lg font-serif font-semibold">Glow Beauty Studio</h4>
                <p className="text-xs text-foreground-secondary">Threading & Waxing</p>
              </div>
            </div>
          </div>
          <div>
            <h5 className="font-medium mb-4">Contact</h5>
            <div className="space-y-2 text-sm text-foreground-secondary">
              <div className="flex items-center gap-2">
                <Phone size={16} weight="light" />
                <a href="tel:7705551234" className="hover:text-accent transition-colors">(770) 555-1234</a>
              </div>
              <div className="flex items-center gap-2">
                <Envelope size={16} weight="light" />
                <a href="mailto:info@glowbeautystudio.com" className="hover:text-accent transition-colors">info@glowbeautystudio.com</a>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={16} weight="light" />
                <span>123 Main Street<br/>Dallas, GA 30132</span>
              </div>
            </div>
          </div>
          <div>
            <h5 className="font-medium mb-4">Hours</h5>
            <div className="space-y-2 text-sm text-foreground-secondary">
              <div className="flex items-center gap-2">
                <Clock size={16} weight="light" />
                <span>Mon-Sat: 10am - 7pm</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={16} weight="light" />
                <span>Sunday: 12pm - 5pm</span>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-8 border-t border-border">
          <div className="text-center text-sm text-foreground-muted mb-4">
            © 2026 Glow Beauty Studio. All rights reserved.
          </div>
          <div className="text-center text-xs text-foreground-muted/60 italic">
            Demo website template for beauty salons
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BeautySalonDemo;
